// Who sees what in the console (AdminClaimsExtensions, ActiveProviderCookie and the pages' Effective* properties):
// QOR's internal staff look at one processor at a time and may pick any client of it; a client's staff always see
// their own client (its programs and locations) and may filter by processor. The API enforces the same (S5.1); the
// pages only ask for what will be allowed.
import { type SessionView } from '@qor/web-shared';
import { type ProgramSummary } from './api';

/** ActiveProviderCookie.AvailableProviders, and every processor picker: ZumRails and Luxara hidden. */
export const PROVIDER_OPTIONS = ['InterCash', 'Central Payments', 'Mdp', 'Gsc'] as const;
export const STATUS_OPTIONS = ['New', 'PendingOnboarding', 'PendingApproval', 'Active', 'Suspended', 'Closed'] as const;
export const TRANSACTION_TYPES = ['Unknown', 'Load', 'Purchase', 'Refund', 'Fee', 'Adjustment'] as const;
export const ROLE_OPTIONS = ['Cardholder', 'ProgramAdmin', 'SupportUser', 'SystemAdmin'] as const;
export const STAFF_ROLE_OPTIONS = ROLE_OPTIONS.filter((r) => r !== 'Cardholder');
export const PROGRAM_FEATURES = [
  'CheckBalance',
  'AddMoney',
  'Transactions',
  'CardSettings',
  'EditProfile',
  'PointsToCash',
] as const;

export interface Scope {
  internal: boolean;
  /** Internal staff only: the processor they look at (null until chosen). */
  activeProvider: string | null;
  /** Internal staff who have not chosen a processor yet: the pages ask them to first. */
  needsProvider: boolean;
  /** A client's staff: their own program. */
  ownProgramId: string | null;
}

export function scopeOf(view: Pick<SessionView, 'user' | 'activeProvider'> | undefined): Scope {
  const internal = view?.user?.internal === true;
  const activeProvider = internal ? (view?.activeProvider ?? null) : null;
  return {
    internal,
    activeProvider,
    needsProvider: internal && activeProvider === null,
    ownProgramId: view?.user?.programId ?? null,
  };
}

/** The processor sent to the API: the chosen one for internal staff, the page's filter for a client's staff. */
export const effectiveProvider = (scope: Scope, provider: string | null | undefined) =>
  scope.internal ? scope.activeProvider : provider || null;

/** The client sent to the API: the page's filter for internal staff, always their own for a client's staff. */
export const effectiveProgramId = (scope: Scope, programId: string | null | undefined) =>
  scope.internal ? programId || null : scope.ownProgramId;

/** The "All clients" filter: top-level clients of the chosen processor (a client includes its locations). */
export const clientOptions = (programs: readonly ProgramSummary[], scope: Scope) =>
  programs.filter(
    (p) => p.parentProgramId === null && (scope.activeProvider === null || p.provider === scope.activeProvider),
  );

/** Where a new cardholder may go: the chosen processor's programs, or the own client and its locations. */
export function assignablePrograms(programs: readonly ProgramSummary[], scope: Scope) {
  const visible = programs.filter((p) => p.provider !== 'ZumRails');
  return scope.internal
    ? visible.filter((p) => p.provider === scope.activeProvider)
    : visible.filter((p) => p.id === scope.ownProgramId || p.parentProgramId === scope.ownProgramId);
}

/** PartnerDetailModel.FeatureLabel */
export function featureLabel(feature: string): string {
  switch (feature) {
    case 'CheckBalance':
      return 'Check balance';
    case 'AddMoney':
      return 'Add money';
    case 'Transactions':
      return 'View transactions';
    case 'CardSettings':
      return 'Card settings (lock / unlock / replace)';
    case 'EditProfile':
      return 'Edit profile';
    case 'PointsToCash':
      return 'Points-to-cash redemption';
    default:
      return feature;
  }
}

/** AuditLogModel.ActionBadgeClass */
export function actionBadgeClass(action: string): string {
  if (['Rejected', 'Frozen', 'Removed', 'Disabled'].some((w) => action.includes(w))) return 'qc-badge-warning';
  if (action.includes('Check')) return 'qc-badge-purple';
  if (['Created', 'Activate', 'Approved', 'Enabled', 'Unfrozen'].some((w) => action.includes(w)))
    return 'qc-badge-success';
  return 'qc-badge-accent';
}
